import i18n from '@/utils/i18n';
import React, { useState } from 'react';
import {
  Badge,
  Icons,
  SegmentedControl,
  Typography,
  XStack,
  YStack,
} from '@usepepper/sprout-kit-ui';
import { InsightCard } from '@/experiences/sales-portal/domain/insights/components/insight-card';
import {
  InsightGrowthDirection,
  ProfitRollupHubCardData,
} from '@/experiences/sales-portal/domain/insights/blocs/types/insight';
import { PriceTrendsDisplayType } from '@/enums';
import { useIsMobileBottomNavLayout } from '@/experiences/sales-portal/blocs/hooks/use-is-mobile-bottom-nav-layout';

const DELTA_ICON_SIZE = 12;

const DELTA_BADGE_COLOR: Record<
  InsightGrowthDirection,
  'error' | 'muted' | 'success'
> = {
  [InsightGrowthDirection.Down]: 'error',
  [InsightGrowthDirection.Flat]: 'muted',
  [InsightGrowthDirection.Up]: 'success',
};

const DELTA_BADGE_ICON: Partial<
  Record<InsightGrowthDirection, typeof Icons.ArrowUp>
> = {
  [InsightGrowthDirection.Down]: Icons.ArrowDown,
  [InsightGrowthDirection.Up]: Icons.ArrowUp,
};

export interface ProfitRollupCardProps {
  data: ProfitRollupHubCardData;
}

export const ProfitRollupCard: React.FC<ProfitRollupCardProps> = ({ data }) => {
  const isPhone = useIsMobileBottomNavLayout();
  const [displayType, setDisplayType] = useState<PriceTrendsDisplayType>(
    PriceTrendsDisplayType.Price,
  );

  const showToggle = data.avgGpPercentage !== undefined;
  const showGrossProfit =
    showToggle && displayType === PriceTrendsDisplayType.GP;

  const stat =
    (showGrossProfit ? data.avgGpPercentage : undefined) ?? data.totalSales;
  const DeltaIcon = stat.delta
    ? DELTA_BADGE_ICON[stat.delta.direction]
    : undefined;

  const handleDisplayTypeChange = (value: string) => {
    if (
      value === PriceTrendsDisplayType.GP ||
      value === PriceTrendsDisplayType.Price
    ) {
      setDisplayType(value);
    }
  };

  return (
    <InsightCard
      actionsElement={
        showToggle ? (
          <SegmentedControl
            aria-label={i18n.t('price_trends')}
            items={[
              {
                label: i18n.t('price_trends.price'),
                value: PriceTrendsDisplayType.Price,
              },
              {
                label: i18n.t('price_trends.gross_profit'),
                value: PriceTrendsDisplayType.GP,
              },
            ]}
            onValueChange={handleDisplayTypeChange}
            size="$sm"
            value={displayType}
          />
        ) : undefined
      }
      subtitle={data.subtitle}
      title={data.title}
    >
      <YStack
        alignItems="center"
        gap="$8"
        marginBottom={isPhone ? '$4' : '$32'}
        marginTop="$32"
      >
        <Typography size="$3xl" variant="heading">
          {stat.formattedValue}
        </Typography>
        {stat.delta ? (
          <XStack alignItems="center" gap="$4">
            <XStack alignItems="center">
              <Badge
                color={DELTA_BADGE_COLOR[stat.delta.direction]}
                icon={
                  DeltaIcon ? <DeltaIcon size={DELTA_ICON_SIZE} /> : undefined
                }
                size="$sm"
              >
                {stat.delta.formattedValue}
              </Badge>
            </XStack>
            <Typography muted size="$sm">
              {i18n.t('insights_hub.weekly_profit_rollup.wow')}
            </Typography>
          </XStack>
        ) : null}
      </YStack>
    </InsightCard>
  );
};
